import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/feature/JsonLd';
import { articleContents } from '@/mocks/articleContent';
import { allArticles } from '@/mocks/articles';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://superbuyspread.rest';
const faviconUrl = 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/67defc9f4dfae2c04004e8ea78b88107.png';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = slug ? articleContents[slug] : undefined;

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-16">
          <div className="container-custom text-center">
            <h1 className="font-heading text-3xl font-bold text-foreground-900 mb-4">Article Not Found</h1>
            <p className="text-foreground-600 mb-6">The article you are looking for does not exist or has been moved.</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Homepage
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const articleUrl = `${siteUrl}/article/${article.slug}`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: article.title,
    description: article.introduction,
    url: articleUrl,
    datePublished: article.datePublished,
    dateModified: article.datePublished,
    author: {
      '@type': 'Organization',
      name: 'Superbuy Spreadsheet',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Superbuy Spreadsheet',
      logo: {
        '@type': 'ImageObject',
        url: faviconUrl,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
    keywords: article.keywords,
  };

  const relatedArticles = allArticles.filter(
    (a) => article.relatedArticles.includes(a.slug)
  );

  const tagLabels: Record<string, string> = {
    'superbuy-spreadsheet-beginner-guide': 'Beginner Guide',
    'superbuy-spreadsheet-product-organization': 'Organization',
    'superbuy-spreadsheet-comparison-guide': 'Comparison',
    'superbuy-spreadsheet-qc-guide': 'Quality Control',
    'superbuy-spreadsheet-shipping-guide': 'Shipping',
    'superbuy-spreadsheet-best-finds': 'Curated Finds',
  };
  const tagLabel = tagLabels[article.slug] || 'Guide';

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={articleSchema} />
      <Navbar />

      <main>
        <article>
          <header className="pt-24 pb-12 md:pb-16 bg-background-100">
            <div className="container-custom max-w-3xl">
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 text-foreground-500 hover:text-primary-600 text-xs font-medium mb-6 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-s-line"></i>
                Back to Superbuy Spreadsheet Homepage
              </Link>

              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500 mb-6">
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.datePublished}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  {article.readTime}
                </span>
                <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">
                  {tagLabel}
                </span>
              </div>

              <p className="text-foreground-600 text-sm md:text-base leading-relaxed italic border-l-3 border-accent-300 pl-4">
                {article.introduction}
              </p>
            </div>
          </header>

          <div className="container-custom max-w-3xl py-10 md:py-14">
            <div className="prose-content">
              {article.sections.map((section, idx) => (
                <section key={idx} className="mb-10">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4" id={`section-${idx}`}>
                    {section.heading}
                  </h2>
                  <p className="text-foreground-700 text-sm md:text-base leading-relaxed">
                    {section.content}
                  </p>
                </section>
              ))}
            </div>

            <div className="mt-12 p-6 rounded-xl bg-accent-50 border border-accent-200/50">
              <h3 className="font-heading font-semibold text-foreground-900 mb-3 text-sm">Conclusion</h3>
              <div
                className="text-foreground-700 text-sm leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.conclusion }}
              />
            </div>

            <div className="mt-12 pt-8 border-t border-background-200/70">
              <h3 className="font-heading font-semibold text-foreground-900 mb-2 text-sm">Related Articles</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    className="flex items-start gap-3 p-4 rounded-lg bg-background-100 border border-background-200/70 hover:border-accent-300 transition-all cursor-pointer group"
                  >
                    <span className="w-8 h-8 flex items-center justify-center rounded-md bg-accent-100 text-accent-700 flex-shrink-0 mt-0.5">
                      <i className="ri-file-text-line text-sm"></i>
                    </span>
                    <div>
                      <h4 className="font-heading font-medium text-foreground-900 text-xs group-hover:text-primary-700 transition-colors leading-snug">
                        {ra.title}
                      </h4>
                      <p className="text-foreground-500 text-[11px] mt-1">{ra.readTime}</p>
                    </div>
                  </Link>
                ))}
                <Link
                  to="/"
                  className="flex items-start gap-3 p-4 rounded-lg bg-primary-50 border border-primary-200/50 hover:border-primary-300 transition-all cursor-pointer group"
                >
                  <span className="w-8 h-8 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 flex-shrink-0 mt-0.5">
                    <i className="ri-home-4-line text-sm"></i>
                  </span>
                  <div>
                    <h4 className="font-heading font-medium text-foreground-900 text-xs group-hover:text-primary-700 transition-colors leading-snug">
                      Superbuy Spreadsheet Homepage
                    </h4>
                    <p className="text-foreground-500 text-[11px] mt-1">All guides, categories &amp; resources</p>
                  </div>
                </Link>
              </div>
            </div>

            <div className="mt-10 text-center">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-primary-500/25"
              >
                Visit Superbuy Sheet
                <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}